import type { ComponentProps, Ref } from "react";
import { memo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { Text } from "@similarweb/ui-components/foundations";

import { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";

import { LEAD_SCORE_TYPE_CONFIG } from "../../configuration/score-type";

import { StyledBarContainer, StyledBarItem, StyledLeadScoreChip } from "./styles";

interface ILeadScoreChipProps extends Partial<ComponentProps<typeof Text>> {
    score?: LeadPriorityScore;
    isClickable?: boolean;
    isActive?: boolean;
    hideLabel?: boolean;
    ref?: Ref<HTMLDivElement>;
    onClick?: VoidFunction;
}

const LeadScoreChip = ({
    score = LeadPriorityScore.Unlock,
    onClick,
    isClickable,
    isActive,
    hideLabel,
    ref,
    ...textProps
}: ILeadScoreChipProps) => {
    const { translate } = useTranslate();

    const config = LEAD_SCORE_TYPE_CONFIG[score] || LEAD_SCORE_TYPE_CONFIG[LeadPriorityScore.Unlock];

    const hasBars = score !== LeadPriorityScore.Unlock && score !== LeadPriorityScore.NoScore;

    const isLabelHidden = Boolean(hideLabel) && hasBars;

    return (
        <StyledLeadScoreChip
            fontVariant="sectionTitle"
            color={config.textColor}
            {...textProps}
            ref={ref}
            $isClickable={isClickable}
            $isActive={isActive}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={onClick}
        >
            {hasBars ? (
                <StyledBarContainer>
                    {Array.from({ length: 3 }).map((_, index) => (
                        <StyledBarItem
                            height={config.heights[index]}
                            color={config.colors[index]}
                            key={index}
                        />
                    ))}
                </StyledBarContainer>
            ) : null}

            {isLabelHidden ? null : translate(config.labelKey)}
        </StyledLeadScoreChip>
    );
};

export default memo(LeadScoreChip);
